/**
 * Die gemeinsame Ladefehler-Flaeche (Fehleranzeige Paket 2, Spec
 * `docs/superpowers/specs/2026-10-23-fehleranzeige-ladefehler-design.md`, Regel L1 und Abschnitt
 * "Die Ladefehler-Flaeche"). Hat eine Ansicht eine eigene Flaeche, in der die Daten stehen wuerden,
 * steht bei einem Ladefehler dort - statt des Leertexts - WAS nicht geladen werden konnte und WARUM,
 * samt "Erneut laden", wenn die Flaeche selbst neu laden kann. Eine Komponente fuer alle L1-Faelle,
 * damit es keine sechs Varianten gibt. Vorbild fuer Optik und `role="alert"` ist die Fehlerzeile von
 * `AktivitaetenPage/AktivitaetenSeite.tsx` (Phase "fehler").
 *
 * - `was`: der feste Satz ("Die Historie konnte nicht geladen werden."), `ursache`: das Fehlerobjekt.
 *   Der Text ist `fehlertext(ursache, was)`. Ergibt der `null` (Abbruch, 401), rendert die Komponente
 *   NICHTS: ein schneller Drawer- oder Filterwechsel erzeugt keine Flaeche, und beim 401 uebernimmt
 *   die Anmeldemaske (L5, "Kein Laerm").
 * - Die Flaeche meldet NIE in den Fehlerstapel (L1, "Kein Doppel").
 * - `onErneutLaden` (optional): ohne ihn gibt es keinen Knopf. Der Knopf ist gesperrt, solange das
 *   gelieferte Promise laeuft. Gelingt der Versuch, nimmt der Aufrufer die Flaeche aus dem Baum; die
 *   Komponente entscheidet das nicht selbst. Scheitert der Versuch, setzt der Aufrufer eine neue
 *   `ursache` (oder dieselbe); bedienbar wird der Knopf erst, wenn das Promise erfuellt ist - eine
 *   neue `ursache` allein hebt die Sperre nicht auf. Steht dieselbe Flaeche nacheinander fuer
 *   wechselnde Gegenstaende (Bestellung im Drawer), setzt der Aufrufer `key` auf den Gegenstand,
 *   sonst bleibt der Knopf fuer B gesperrt, solange der Lauf fuer A laeuft. Ein ABGELEHNTES Promise faengt die
 *   Komponente nicht ab: der Aufrufer behandelt seinen Ladefehler selbst, alles andere ist ein
 *   Programmfehler und gehoert dem globalen Auffaenger.
 */
import { useMemo, useState, type JSX } from "react";
import { fehlertext } from "../../lib/fehlertext.ts";

export interface LadefehlerProps {
  /** Was nicht geladen werden konnte, als ganzer Satz mit Punkt ("Die Historie konnte nicht geladen werden."). */
  was: string;
  /** Das gefangene Fehlerobjekt; der Grund dahinter kommt aus `fehlertext()`. */
  ursache: unknown;
  /** Der Neuversuch der Flaeche. Ohne ihn erscheint kein Knopf. */
  onErneutLaden?: () => Promise<unknown> | void;
}

export function Ladefehler({ was, ursache, onErneutLaden }: LadefehlerProps): JSX.Element | null {
  const [laeuft, setLaeuft] = useState(false);
  // `fehlertext` meldet einen Programmfehler mit `console.error`; ohne Memo geschaehe das bei jedem
  // Neuzeichnen (etwa beim Sperren des Knopfes) erneut.
  const text = useMemo(() => fehlertext(ursache, was), [ursache, was]);
  if (text === null) return null;

  async function erneutLaden(): Promise<void> {
    if (laeuft || onErneutLaden === undefined) return;
    setLaeuft(true);
    try {
      await onErneutLaden();
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <div className="ladefehler" role="alert">
      <p className="ladefehler-text">{text}</p>
      {onErneutLaden !== undefined ? (
        <button
          type="button"
          className="btn-ghost ladefehler-knopf"
          disabled={laeuft}
          onClick={() => void erneutLaden()}
        >
          Erneut laden
        </button>
      ) : null}
    </div>
  );
}
